import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Logo } from '../components/Logo';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Lock, Eye, EyeOff, CheckCircle, XCircle, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ResetPassword() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenError, setTokenError] = useState('');
  const [success, setSuccess] = useState(false);
  const [errors, setErrors] = useState({});

  // Verify token on mount
  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setTokenError(t('auth.invalidResetLink'));
        setVerifying(false);
        return;
      }

      try {
        const response = await fetch(`${API}/api/auth/verify-reset-token?token=${token}`);
        const data = await response.json();

        if (data.valid) {
          setTokenValid(true);
        } else {
          setTokenError(data.message || t('auth.invalidResetLink'));
        }
      } catch (err) {
        setTokenError(t('errors.serverError'));
      } finally {
        setVerifying(false);
      }
    };

    verifyToken();
  }, [token, t]);

  const validateForm = () => {
    const newErrors = {};

    if (!password) {
      newErrors.password = t('auth.invalidPassword');
    } else if (password.length < 6) {
      newErrors.password = t('errors.passwordLength');
    }

    if (!confirmPassword) {
      newErrors.confirmPassword = t('auth.confirmPasswordRequired');
    } else if (password !== confirmPassword) {
      newErrors.confirmPassword = t('auth.passwordsMismatch');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          new_password: password
        })
      });

      if (response.ok) {
        setSuccess(true);
        toast.success(t('auth.passwordResetSuccess'));
        setTimeout(() => navigate('/login'), 3000);
      } else {
        const data = await response.json();
        toast.error(data.detail || t('errors.serverError'));
      }
    } catch (err) {
      toast.error(t('errors.serverError'));
    } finally {
      setLoading(false);
    }
  };

  // Loading state
  if (verifying) {
    return (
      <div className="min-h-screen pt-24 pb-16 bg-[#F9FAFB] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-[#0A2540] mx-auto mb-4" />
          <p className="text-slate-600">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  // Invalid token state
  if (!tokenValid) {
    return (
      <div className="min-h-screen pt-24 pb-16 bg-[#F9FAFB] flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md px-4"
        >
          <div className="bg-white rounded-3xl shadow-soft p-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-8 h-8 text-red-600" />
            </div>
            <h1 className="font-heading text-2xl font-bold text-slate-900 mb-2">
              {t('auth.invalidResetLink')}
            </h1>
            <p className="text-slate-600 mb-6">
              {tokenError || t('auth.resetLinkExpired')}
            </p>
            <Link to="/forgot-password">
              <Button className="btn-primary">
                {t('auth.requestNewLink')}
              </Button>
            </Link>
          </div>
        </motion.div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen pt-24 pb-16 bg-[#F9FAFB] flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md px-4"
        >
          <div className="bg-white rounded-3xl shadow-soft p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h1 className="font-heading text-2xl font-bold text-slate-900 mb-2">
              {t('auth.passwordResetSuccess')}
            </h1>
            <p className="text-slate-600 mb-6">
              {t('auth.redirectingToLogin')}
            </p>
            <Loader2 className="w-6 h-6 animate-spin text-[#0A2540] mx-auto" />
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16 bg-[#F9FAFB] flex items-center justify-center">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-md px-4"
      >
        <div className="bg-white rounded-3xl shadow-soft p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <Link to="/" className="inline-block mb-6">
              <Logo size="large" />
            </Link>
            <h1 className="font-heading text-3xl font-bold text-slate-900 mb-2">
              {t('auth.resetPassword')}
            </h1>
            <p className="text-slate-600">
              {t('auth.enterNewPassword')}
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label htmlFor="password" className="text-slate-700">
                {t('auth.newPassword')}
              </Label>
              <div className="relative mt-1">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (errors.password) setErrors({ ...errors, password: null });
                  }}
                  className={`pl-12 pr-12 rounded-xl h-12 ${errors.password ? 'border-red-500' : ''}`}
                  placeholder="••••••••"
                  data-testid="new-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {errors.password && (
                <p className="text-red-500 text-sm mt-1">{errors.password}</p>
              )}
            </div>

            <div>
              <Label htmlFor="confirmPassword" className="text-slate-700">
                {t('auth.confirmPassword')}
              </Label>
              <div className="relative mt-1">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="confirmPassword"
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    if (errors.confirmPassword) setErrors({ ...errors, confirmPassword: null });
                  }}
                  className={`pl-12 rounded-xl h-12 ${errors.confirmPassword ? 'border-red-500' : ''}`}
                  placeholder="••••••••"
                  data-testid="confirm-password-input"
                />
              </div>
              {errors.confirmPassword && (
                <p className="text-red-500 text-sm mt-1">{errors.confirmPassword}</p>
              )}
            </div>

            <Button
              type="submit"
              className="w-full btn-primary py-6"
              disabled={loading}
              data-testid="reset-submit"
            >
              {loading ? t('common.loading') : t('auth.resetPassword')}
            </Button>
          </form>
        </div>
      </motion.div>
    </div>
  );
}
